import { IonButton, IonCard, IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import { CreateTeamInput, CreateTeamMemberInput, TeamPermissionLevel, TeamRole, TeamStatus, UpdateMembershipInput } from "../../../API";
import { Membership, OrganizationMembershipType, Team } from "../../../models";
import { updateMembership } from "../../../utilities/membership/Membership";
import ErrorAlert from "../../Errors/ErrorAlert";
import Spinner from "../../Spinners/Spinner";
import CONSTANT from "../../../constant/constant";
import { createTeam } from "../../../utilities/team/Team";
import constants from "../../../constant/constant";
import { PersonContext } from "../../../context/PersonContext";
import { createTeamMember } from "../../../utilities/teamMember/TeamMember";

interface _Props {
    membershipType: OrganizationMembershipType | undefined;
    membership: Membership | undefined;
    setMembership: Function
};

const EditMembershipApplicationPayment: React.FC<_Props> = ({membershipType, membership, setMembership}) => {
    const user = useContext(PersonContext);
    
    const [error, setError] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [membershipPrice, setMembershipPrice] = useState<number>();
    const [amountPaid, setAmountPaid] = useState<number>();
    const [isAELOrganization, setIsAELOrganization] = useState(false);

    useEffect(() => {
        if(membership) {
            setAmountPaid(membership.amountPaid || 0);
        }
    }, [membership]);

    useEffect(() => {
        if (membershipType) setMembershipPrice(membershipType.price || 0);
        if (membershipType && membershipType.organizationId === constants.AEL_ORGANIZATION.id) setIsAELOrganization(true);
    }, [membershipType]);

    const handleCreateTeam = async (updatedMembership: Membership) => {
        const teamInput: CreateTeamInput = {
            name: updatedMembership.groupContact?.name || "Team",
            creatorId: updatedMembership.createdBy || "",
            teamContactId: updatedMembership.groupContactId || ""
        };
        const createTeamResult = await createTeam(teamInput);
        if (createTeamResult.isSuccess) {
            const newTeam: Team = createTeamResult.result;
            // Update the membership to include the new team id
            const updateInput: UpdateMembershipInput = {
                id: updatedMembership.id,
                teamId: newTeam.id
            };
            const updateResult = await updateMembership(updateInput);
            if (!updateResult.isSuccess) {
                const message = "An error occurred. The team could not be saved to the membership. " + updateResult.message;
                setError(message); 
            }
        } else {
            const message = "An error occurred. The team could not be created. " + createTeamResult.message;
            setError(message);
        }
    }

    const getTeamRole = (role: string) => {
        if (role) {
            if (role === "HEAD_COACH" || role === "headCoach") return TeamRole.headCoach;
            else if (role === "ASSISTANT_COACH" || role === "assistantCoach") return TeamRole.assistantCoach;
            else if (role === "PARENT_VOLUNTEER" || role === "parentVolunteer") return TeamRole.parentVolunteer;
            else return null;
        } 
        return null;
    }

    const handleCreateTeamMember = async (updatedMembership: Membership) => {
        if (updatedMembership.teamId) {
            if (updatedMembership.personId) {
                const teamMemberInput: CreateTeamMemberInput = {
                    teamId: updatedMembership.teamId,
                    personId: updatedMembership.personId,
                    status: TeamStatus.active,
                    permissionLevel: updatedMembership?.organizationMembershipType?.name.toLowerCase().includes("coach") ? TeamPermissionLevel.admin : TeamPermissionLevel.member,
                    role: updatedMembership.aelTeamRole ? getTeamRole(updatedMembership.aelTeamRole) : null,
                    creatorId: user.id
                };
                const createResult = await createTeamMember(teamMemberInput);
                console.log("createResult: ", createResult);
                if (!createResult.isSuccess) {
                    const message = "An error occurred. The new team member could not be created. " + createResult.message;
                    setError(message);
                }
            }
        }
    }

    const handleCompletePaymentAndUpdateStatus = async () => {
        if(membership) {
            setIsLoading(true);
            const input: UpdateMembershipInput = {
                id: membership.id,
                amountPaid: membershipPrice,
                membershipStatus: CONSTANT.MEMBERSHIP.APPLICATION_STATUS.COMPLETE
            };
            const updateMembershipResult = await updateMembership(input);
            if (updateMembershipResult.isSuccess) {
                setMembership(updateMembershipResult.result);

                if (isAELOrganization && membershipType?.category === "group") {
                    // First, create the team
                    await handleCreateTeam(membership);
                } 
                
                // Check for AEL team member creation
                if (isAELOrganization && membershipType?.category === "individual") {
                    // First, create the teamMember membership
                    await handleCreateTeamMember(membership);
                }
            } else {
                setError(updateMembershipResult.message);
            }
            setIsLoading(false);
        }
    }

    return (
        <React.Fragment>
            <IonCard color="white" className="ion-padding">
                <h3>Payment</h3>
                {error && <ErrorAlert width="12" error={error} />}
                {isLoading ? 
                    <Spinner/>
                :
                    <>
                        <IonRow>
                            <IonCol sizeXs="12" sizeMd="6">
                                <IonItem color="white">
                                    <IonLabel position="stacked">Membership Type Price</IonLabel>
                                        <IonInput
                                            id="membership-type-edit-price"
                                            type="text"
                                            disabled={true}
                                            value={membershipPrice}
                                            aria-required={true}
                                        />
                                </IonItem>
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="6">
                                <IonItem color="white">
                                    <IonLabel position="stacked">Amount Paid</IonLabel>
                                        <IonInput
                                            id="membership-amount-paid"
                                            type="text"
                                            value={amountPaid}
                                            disabled={true}
                                            aria-required={true}
                                            onIonChange={e => {
                                                setAmountPaid(e.detail.value! ? parseInt(e.detail.value!) : 0);
                                            }}
                                        />
                                </IonItem>
                            </IonCol>
                        </IonRow>
                        {/* <IonRow className="ion-justify-content-center mb-5 mt-4">
                            <IonCol sizeXs="12" sizeMd="3" className="ion-text-center">
                                <IonButton id="edit-membership-payment-save-btn" expand="block" color="tertiary" onClick={handleEditMembershipPayment}>
                                    Save
                                </IonButton>
                            </IonCol>
                        </IonRow> */}
                        {amountPaid !== membershipPrice && (
                            <>
                                <hr />
                                <IonRow className="ion-justify-content-center mb-5 mt-4">
                                    <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                                        <IonButton id="mark-fully-paid-btn" expand="block" color="tertiary" onClick={handleCompletePaymentAndUpdateStatus}>
                                            Mark as Fully Paid and Complete
                                        </IonButton>
                                    </IonCol>
                                </IonRow>
                            </>
                        )}
                    </>
                }
            </IonCard>
        </React.Fragment>
    );
};

export default EditMembershipApplicationPayment;
